import { MODEL_NAMES } from '../constants/index';
import { all, task, timeout } from 'ember-concurrency';
import { cached, tracked } from '@glimmer/tracking';
import { convertToUser } from '../utils/messaging/user';
import { findConversationByContact } from '../utils/messaging/conversation';
import { isEmberTesting } from 'ember-simplepractice/utils/is-testing';
import {
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../utils/messaging/infinity-model';
import Service, { service } from '@ember/service';

const TOKEN_REFRESH_INTERVAL_MINS = 4.5;
const CONTACT_PAGE_SIZE = 15;
const CONTACT_ROLE_FILTER = {
  role: ['primary clinician', 'couple', 'primary clinician for contact'],
};

const optionDefaults = {
  eventHandlers: {},
  messagingApiUrl: '',
};

export default class MessagingService extends Service {
  @service infinity;
  @service messagingNavigation;
  @service('websocket/messagingWebsocket') messagingWebsocket;
  @service store;

  @tracked conversations;
  @tracked currentProfile;
  @tracked settingAwayStatus = false;
  @tracked contacts = [];
  options = optionDefaults;
  token;

  @cached
  get sortedConversations() {
    if (!this.conversations?.content) return [];

    const unreadIds = new Set(this.currentProfile?.unreadConversationIds || []);
    const draftIds = new Set(this.conversations.content.filter(c => c.hasMessageDraft).map(c => c));

    return this.conversations.content.toSorted((a, b) => {
      return (
        draftIds.has(b) - draftIds.has(a) ||
        unreadIds.has(b.id) - unreadIds.has(a.id) ||
        new Date(b.updatedAt) - new Date(a.updatedAt)
      );
    });
  }

  get messagingApiUrl() {
    return this.options.messagingApiUrl;
  }

  get tokenRefreshIntervalMins() {
    return isEmberTesting() ? 0 : TOKEN_REFRESH_INTERVAL_MINS;
  }

  get unreadConversationCount() {
    return this.currentProfile?.unreadConversationIds?.length || 0;
  }

  get isAway() {
    return this.currentProfile?.isAway || false;
  }

  startTask = task({ drop: true }, async (options = {}) => {
    this.options = { ...optionDefaults, ...options };
    await this.loadApp.perform();
  });

  stop() {
    this.pollMessagingToken.cancelAll();
    this.messagingWebsocket.closeSocket();
  }

  emitEvent(eventName, data) {
    const messagingClient = document.querySelector('#ember-messaging-client');
    const event = new CustomEvent('messagingEvent', { detail: { name: eventName, data } });
    messagingClient.dispatchEvent(event);
  }

  loadApp = task({ drop: true }, async () => {
    await this.getMessagingToken.perform();
    await this.getCurrentProfile.perform();
    await this.getConversations.perform();

    if (this.currentProfile.isClient) {
      await this.getContacts.perform(CONTACT_ROLE_FILTER);
      await this.populateVirtualConversations.perform();
      if (this.conversations.length === 1) {
        await this.messagingNavigation.toConversation(this.conversations.firstObject);
      }
    }

    this.pollMessagingToken.perform();
    this.messagingWebsocket.openSocket(this.messagingApiUrl, this.token);
  });

  getMessagingToken = task({ drop: true }, async () => {
    const response = await fetch('/messaging/tokens', {
      method: 'POST',
    });
    const json = await response.json();
    this.token = json.token;
  });

  getCurrentProfile = task({ drop: true }, async () => {
    this.currentProfile = await this.store.queryRecord(MODEL_NAMES.PROFILE, {
      include: 'system_user',
    });

    let user = this.store
      .peekAll(MODEL_NAMES.USER)
      .find(user => user.id === this.currentProfile.id);
    if (!user) {
      user = this.store.push({
        data: {
          id: this.currentProfile.id,
          type: MODEL_NAMES.USER,
          attributes: {
            name: this.currentProfile.name,
            externalScope: this.currentProfile.externalScope,
            externalId: this.currentProfile.externalId,
            organizationId: this.currentProfile.organizationId,
          },
        },
      });
    }
    this.currentProfile.user = user;
  });

  getConversations = task({ drop: true }, async () => {
    const model = await this.infinity.model(
      MODEL_NAMES.CONVERSATION,
      {
        include: 'users,last_message',
        ...pageNumberInfinityOptions(),
      },
      pageNumberInfinityModel()
    );

    this.#updateConversations(model);
  });

  getContacts = task({ drop: true }, async (filter = null) => {
    const model = await this.infinity.model(
      MODEL_NAMES.CONTACT,
      {
        ...(filter && { filter }),
        include: 'couple',
        ...pageNumberInfinityOptions(CONTACT_PAGE_SIZE),
      },
      pageNumberInfinityModel(CONTACT_PAGE_SIZE)
    );

    this.contacts = model;
  });

  populateVirtualConversations = task({ drop: true }, async () => {
    if (!this.contacts) {
      return;
    }
    const contactsWithoutConversations = this.contacts.filter(
      contact => !findConversationByContact(this.conversations, contact)
    );

    const virtualConversations = contactsWithoutConversations.map(contact =>
      this.buildVirtualConversation(contact)
    );
    this.conversations.unshiftObjects(virtualConversations);
  });

  buildVirtualConversation = contact => {
    let users = [this.currentProfile?.user];
    if (contact.couple.length) {
      users.push(...contact.couple.map(c => convertToUser(c, this.store)));
    } else {
      users.push(convertToUser(contact, this.store));
    }
    return this.store.createRecord(MODEL_NAMES.CONVERSATION, {
      users,
    });
  };

  findOrCreateConversationWithContact(contact) {
    const existingConversation = findConversationByContact(this.conversations, contact);

    if (existingConversation) {
      return existingConversation;
    }

    return this.buildVirtualConversation(contact);
  }

  refreshProfileAndConversations = task({ drop: true }, async () => {
    return all([this.getCurrentProfile.perform(), this.getConversations.perform()]);
  });

  toggleAwayStatus = task({ drop: true }, async isAway => {
    await this.store
      .adapterFor(MODEL_NAMES.PROFILE)
      .updateAwayStatus(this.currentProfile.id, isAway);

    this.currentProfile.isAway = isAway;
  });

  updateAwayMessage = task({ drop: true }, async awayMessage => {
    await this.store
      .adapterFor(MODEL_NAMES.PROFILE)
      .updateAwayMessage(this.currentProfile.id, awayMessage);

    this.currentProfile.awayMessageText = awayMessage;
  });

  pollMessagingToken = task(async () => {
    do {
      await timeout(this.tokenRefreshIntervalMins * 60 * 1000);
      await this.getMessagingToken.perform();
    } while (!isEmberTesting());
  });

  loadMoreConversations = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    let data = await this.infinity.loadNextPage(model);

    this.#updateConversations(data);
  });

  loadMoreContacts = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    await this.infinity.loadNextPage(model);
  });

  #updateConversations(data) {
    const filteredData = data.filter(conversation => {
      const { disabled, inactive } = conversation;

      return !disabled && !(this.currentProfile.isClient && inactive);
    });

    this.conversations = this.infinity.replace(data, filteredData);
  }
}
